import {
  ApolloClient,
  ApolloProvider as BaseApolloProvider,
  HttpLink,
  InMemoryCache,
  from,
  type NormalizedCacheObject,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { getCalendarDateTypePolicies } from '@orn/shared-web/lib/apollo/calendarDateTypePolicies';
import { isAbortedOrOfflineFetch } from '@orn/shared-web/lib/apollo/isAbortedOrOfflineFetch';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import { IS_DEV, IS_STAGING } from '../env';
import { getApiBaseUrl } from '../lib/getEnv';
import { getGoogleStartUrl } from '../lib/mopsAuthUrls';

// Single client instance — avoids recreating on StrictMode remounts.
let apolloClient: ApolloClient<NormalizedCacheObject> | null = null;
let isHandlingSessionExpiry = false;

interface IGraphQLErrorLike {
  message: string;
  locations?: unknown;
  extensions?: { code?: unknown };
}

const getGraphQLErrors = (errorResponse: unknown): readonly IGraphQLErrorLike[] | undefined => {
  if (typeof errorResponse !== 'object' || errorResponse === null) {
    return undefined;
  }
  const maybeGraphQLErrors = (errorResponse as { graphQLErrors?: unknown }).graphQLErrors;
  if (!Array.isArray(maybeGraphQLErrors)) {
    return undefined;
  }
  return maybeGraphQLErrors.filter(
    (error): error is IGraphQLErrorLike =>
      typeof error === 'object' &&
      error !== null &&
      'message' in error &&
      typeof (error as { message?: unknown }).message === 'string'
  );
};

const getNetworkError = (errorResponse: unknown): unknown => {
  if (typeof errorResponse !== 'object' || errorResponse === null) {
    return undefined;
  }
  return (errorResponse as { networkError?: unknown }).networkError;
};

const getNetworkStatusCode = (networkError: unknown): number | undefined => {
  if (typeof networkError !== 'object' || networkError === null) {
    return undefined;
  }
  if ('statusCode' in networkError) {
    const statusCode = (networkError as { statusCode?: unknown }).statusCode;
    return typeof statusCode === 'number' ? statusCode : undefined;
  }
  return undefined;
};

const shouldTreatAsUnauthorized = (
  graphQLErrors: readonly IGraphQLErrorLike[] | undefined,
  networkError: unknown
): boolean => {
  const hasAuthGraphQLError =
    graphQLErrors?.some(
      (graphQLError) =>
        graphQLError.extensions?.code === 'UNAUTHENTICATED' || graphQLError.extensions?.code === 'FORBIDDEN'
    ) ?? false;
  if (hasAuthGraphQLError) {
    return true;
  }
  const networkStatusCode = getNetworkStatusCode(networkError);
  return networkStatusCode === 401 || networkStatusCode === 403;
};

const handleSessionExpired = () => {
  if (isHandlingSessionExpiry) {
    return;
  }
  isHandlingSessionExpiry = true;

  void apolloClient?.clearStore().catch((error) => {
    logger.error('Apollo clearStore failed during session expiry', error, {
      tags: { graphql: true, auth: true },
    });
  });

  // Full-page navigation — mops-api `/auth/google/start` is a different
  // origin in dev (different port) and a plain redirect in prod.
  window.location.assign(getGoogleStartUrl());
};

function getApolloClient() {
  if (!apolloClient) {
    const errorLink = onError((errorResponse) => {
      const graphQLErrors = getGraphQLErrors(errorResponse);
      const networkError = getNetworkError(errorResponse);

      if (shouldTreatAsUnauthorized(graphQLErrors, networkError)) {
        handleSessionExpired();
        return;
      }

      const { operation } = errorResponse;
      const operationName = operation.operationName ?? 'Unknown';
      const queryString = operation.query.loc?.source.body ?? 'Unknown query';

      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations }) => {
          const errorMessage = `GraphQL Error (${operationName}): ${message}`;
          logger.error(errorMessage, new Error(errorMessage), {
            tags: { graphql: true, operationName },
            contexts: {
              graphql: { operation: operationName, query: queryString },
              error: { message, locations: JSON.stringify(locations, null, 2) },
            },
          });
        });
      }

      if (networkError) {
        // Drop fetch failures that originate from the user's side (page
        // unload during navigation, tab suspension, lost connectivity,
        // explicit abort). These have no statusCode and produce huge
        // duplicate clusters whenever the session-expiry redirect aborts
        // multiple in-flight queries at once.
        if (isAbortedOrOfflineFetch(networkError) && getNetworkStatusCode(networkError) === undefined) {
          return;
        }

        let message = 'Unknown network error';
        if (networkError instanceof Error) {
          message = networkError.message;
        } else if (typeof networkError === 'string') {
          message = networkError;
        } else if (typeof networkError === 'number' || typeof networkError === 'boolean') {
          message = `${networkError}`;
        }
        const errorMessage = `GraphQL Network Error (${operationName}): ${message}`;
        logger.error(errorMessage, new Error(errorMessage), {
          tags: { graphql: true, networkError: true, operationName },
          contexts: {
            graphql: { operation: operationName, query: queryString },
            network: { statusCode: getNetworkStatusCode(networkError) },
          },
        });
      }
    });

    const httpLink = new HttpLink({
      uri: `${getApiBaseUrl()}/graphql`,
      credentials: 'include',
    });

    apolloClient = new ApolloClient({
      link: from([errorLink, httpLink]),
      cache: new InMemoryCache({
        typePolicies: {
          ...getCalendarDateTypePolicies(),
          // `ReleaseDraft`'s GraphQL ID is `tag`, not `id` — without this,
          // Apollo's default `dataIdFromObject` skips normalization and the
          // checklist-toggle mutation response lands in `ROOT_MUTATION`
          // never merged back into the `adminGetCurrentReleaseDraft` query
          // cache. Operators see the optimistic check flip but the
          // server-rendered marker text takes 30s (until the poll lands)
          // to appear.
          //
          // Delete this entry as part of the DB-backed release-tool
          // replacement (the same PR that removes `ReleasesScene` and
          // friends): the new tool will use a real `id` field and the
          // default normalization works without a typePolicy.
          ReleaseDraft: { keyFields: ['tag'] },
        },
      }),
      devtools: {
        enabled: IS_DEV || IS_STAGING,
      },
    });
  }
  return apolloClient;
}

const ApolloProvider = (props: React.PropsWithChildren) => {
  const client = getApolloClient();
  return <BaseApolloProvider client={client}>{props.children}</BaseApolloProvider>;
};

export default ApolloProvider;
